// src/pages/app/BotFeaturesPage.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bot,
  getBotById,
  updateBot,
  cancelBotSubscription
} from "../../api/bots";
import { useTranslation } from "react-i18next";

const formatCurrency = (amountCents: number, currency: string) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(amountCents / 100);

type BotFeaturesForm = {
  useDomainCrawler: boolean;
  usePdfCrawler: boolean;
  channelWeb: boolean;
  channelWhatsapp: boolean;
  channelInstagram: boolean;
  channelMessenger: boolean;
  useCalendar: boolean;
  leadWhatsappMessages200: boolean;
  leadWhatsappMessages500: boolean;
  leadWhatsappMessages1000: boolean;
};

const BotFeaturesPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bot, setBot] = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState<BotFeaturesForm | null>(null);

  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    getBotById(id)
      .then((data) => {
        setBot(data);

        const initialForm: BotFeaturesForm = {
          useDomainCrawler: data.useDomainCrawler,
          usePdfCrawler: data.usePdfCrawler,
          channelWeb: data.channelWeb,
          channelWhatsapp: data.channelWhatsapp,
          channelInstagram: data.channelInstagram,
          channelMessenger: data.channelMessenger,
          useCalendar: data.useCalendar,
          leadWhatsappMessages200: data.leadWhatsappMessages200,
          leadWhatsappMessages500: data.leadWhatsappMessages500,
          leadWhatsappMessages1000: data.leadWhatsappMessages1000
        };

        setForm(initialForm);
      })
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botFeatures.errors.loadBot"));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const handleToggle =
    (field: keyof BotFeaturesForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!form || !bot) return;

      const value = (e.target as HTMLInputElement).checked;
      const updatedForm: BotFeaturesForm = { ...form, [field]: value };
      setForm(updatedForm);
    };

  const handleCancelSubscription = async () => {
    if (!id) return;

    setCancelLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await cancelBotSubscription(id);
      setBot(updated);
      setSuccess(t("botFeatures.success.subscriptionCanceled"));
    } catch (err: any) {
      console.error("Failed to cancel subscription", err);
      setError(
        err?.message ||
          t("botFeatures.errors.cancelSubscription", "Failed to cancel subscription.")
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bot || !form) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        useDomainCrawler: form.useDomainCrawler,
        usePdfCrawler: form.usePdfCrawler,
        channelWeb: form.channelWeb,
        channelWhatsapp: form.channelWhatsapp,
        channelMessenger: form.channelMessenger,
        channelInstagram: form.channelInstagram,
        useCalendar: form.useCalendar,
        leadWhatsappMessages200: form.leadWhatsappMessages200,
        leadWhatsappMessages500: form.leadWhatsappMessages500,
        leadWhatsappMessages1000: form.leadWhatsappMessages1000
      };

      const updated = await updateBot(bot.id, payload);
      setBot(updated);

      setSuccess(t("botFeatures.success.saved"));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botFeatures.errors.saveFeatures"));
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!bot || !form || !id) return;

    setNextLoading(true);
    setError(null);

    try {
      const payload = {
        useDomainCrawler: form.useDomainCrawler,
        usePdfCrawler: form.usePdfCrawler,
        channelWeb: form.channelWeb,
        channelWhatsapp: form.channelWhatsapp,
        channelMessenger: form.channelMessenger,
        channelInstagram: form.channelInstagram,
        useCalendar: form.useCalendar,
        leadWhatsappMessages200: form.leadWhatsappMessages200,
        leadWhatsappMessages500: form.leadWhatsappMessages500,
        leadWhatsappMessages1000: form.leadWhatsappMessages1000
      };

      const updated = await updateBot(bot.id, payload);
      setBot(updated);

      navigate(`/app/bots/${encodeURIComponent(id)}/plan`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botFeatures.errors.saveFeatures"));
    } finally {
      setNextLoading(false);
    }
  };

  const isActive = bot && bot.status === "ACTIVE";


  const getStatusPillClass = (status: Bot["status"]) => {
    const normalized = status.toUpperCase();
    if (normalized === "ACTIVE") {
      return "plan-summary-status plan-summary-status-ok";
    }
    if (normalized === "DRAFT") {
      return "plan-summary-status plan-summary-status-warn";
    }
    if (normalized === "CANCELLED" || normalized === "INACTIVE") {
      return "plan-summary-status plan-summary-status-error";
    }
    return "plan-summary-status";
  };

  if (loading || !bot || !form) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{t("botFeatures.title")}</h1>
        </div>
        <div className="card">
          <p className="muted">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{t("botFeatures.title")}</h1>
          <p className="muted">
            {t("botFeatures.subtitle", { name: bot.name || "" })}
          </p>
        </div>

        <div className="page-header-actions">
          <Link
            className="btn-secondary"
            to={`/app/bots/${encodeURIComponent(bot.id)}`}
          >
            {t("botFeatures.actions.backToBot")}
          </Link>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="detail-layout">
        <section className="detail-main">
          <form className="form features-form" onSubmit={handleSave}>
            <fieldset className="form-fieldset feature-group">
              <legend>{t("botFeatures.groups.knowledge")}</legend>
              <div className="feature-options">
                <label
                  className={
                    "feature-option" +
                    (form.useDomainCrawler ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.useDomainCrawler}
                    onChange={handleToggle("useDomainCrawler")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.knowledge.domain.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.knowledge.domain.description")}
                        </p>
                      </div>
                    </div>
                  </div>
                </label>

                <label
                  className={
                    "feature-option" +
                    (form.usePdfCrawler ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.usePdfCrawler}
                    onChange={handleToggle("usePdfCrawler")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.knowledge.pdf.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.knowledge.pdf.description")}
                        </p>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </fieldset>

            <fieldset className="form-fieldset feature-group">
              <legend>{t("botFeatures.groups.channels")}</legend>
              <div className="feature-options">
                <label
                  className={
                    "feature-option" +
                    (form.channelWeb ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.channelWeb}
                    onChange={handleToggle("channelWeb")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.channels.web.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.channels.web.description")}
                        </p>
                      </div>
                    </div>
                  </div>
                </label>

                <label
                  className={
                    "feature-option" +
                    (form.channelWhatsapp ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.channelWhatsapp}
                    onChange={handleToggle("channelWhatsapp")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.channels.whatsapp.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.channels.whatsapp.description")}
                        </p>
                      </div>
                    </div>
                  </div>
                </label>

                <label
                  className={
                    "feature-option" +
                    (form.channelMessenger ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.channelMessenger}
                    onChange={handleToggle("channelMessenger")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.channels.messenger.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.channels.messenger.description")}
                        </p>
                      </div>
                      
                    </div>
                  </div>
                </label>

                <label
                  className={
                    "feature-option" +
                    (form.channelInstagram ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.channelInstagram}
                    onChange={handleToggle("channelInstagram")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.channels.instagram.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.channels.instagram.description")}
                        </p>
                      </div>
                      
                    </div>
                  </div>
                </label>
              </div>
            </fieldset>

            <fieldset className="form-fieldset feature-group">
              <legend>{t("botFeatures.groups.calendar")}</legend>
              <div className="feature-options">
                <label
                  className={
                    "feature-option" +
                    (form.useCalendar ? " feature-option-checked" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.useCalendar}
                    onChange={handleToggle("useCalendar")}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.calendar.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.calendar.description")}
                        </p>
                      </div>
                      
                    </div>
                  </div>
                </label>
              </div>
            </fieldset>

            <fieldset className="form-fieldset feature-group">
              <legend>{t("botFeatures.groups.leadAds")}</legend>
              <div className="feature-options">
                <label
                  className={
                    "feature-option" +
                    ((form.leadWhatsappMessages200 ||
                      form.leadWhatsappMessages500 ||
                      form.leadWhatsappMessages1000) &&
                    form.channelWhatsapp
                      ? " feature-option-checked"
                      : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={
                      form.channelWhatsapp &&
                      (form.leadWhatsappMessages200 ||
                        form.leadWhatsappMessages500 ||
                        form.leadWhatsappMessages1000)
                    }
                    disabled={!form.channelWhatsapp}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => {
                        if (!prev || !bot) return prev;

                        let next: BotFeaturesForm;

                        if (!checked) {
                          next = {
                            ...prev,
                            leadWhatsappMessages200: false,
                            leadWhatsappMessages500: false,
                            leadWhatsappMessages1000: false
                          };
                        } else {
                          if (
                            !prev.leadWhatsappMessages200 &&
                            !prev.leadWhatsappMessages500 &&
                            !prev.leadWhatsappMessages1000
                          ) {
                            next = {
                              ...prev,
                              leadWhatsappMessages200: true,
                              leadWhatsappMessages500: false,
                              leadWhatsappMessages1000: false
                            };
                          } else {
                            next = prev;
                          }
                        }

                        return next;
                      });
                    }}
                  />
                  <div className="feature-option-content">
                    <div className="feature-option-header">
                      <div>
                        <div className="feature-option-title">
                          {t("botFeatures.leadAds.whatsapp.title")}
                        </div>
                        <p className="feature-option-description">
                          {t("botFeatures.leadAds.whatsapp.description")}
                        </p>
                      </div>
                    </div>

                    {/* Dropdown for tier */}
                    <div style={{ marginTop: "0.5rem" }}>
                      <select
                        disabled={
                          !form.channelWhatsapp ||
                          !(
                            form.leadWhatsappMessages200 ||
                            form.leadWhatsappMessages500 ||
                            form.leadWhatsappMessages1000
                          )
                        }
                        value={
                          form.leadWhatsappMessages1000
                            ? "1000"
                            : form.leadWhatsappMessages500
                            ? "500"
                            : form.leadWhatsappMessages200
                            ? "200"
                            : ""
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => {
                            if (!prev || !bot) return prev;

                            const next: BotFeaturesForm = {
                              ...prev,
                              leadWhatsappMessages200: v === "200",
                              leadWhatsappMessages500: v === "500",
                              leadWhatsappMessages1000: v === "1000"
                            };
                            return next;
                          });
                        }}
                      >
                        <option value="" disabled>
                          {t("botFeatures.leadAds.whatsapp.selectTier")}
                        </option>
                        <option value="200">
                          200 WhatsApp messages / month
                        </option>
                        <option value="500">
                          500 WhatsApp messages / month
                        </option>
                        <option value="1000">
                          1000 WhatsApp messages / month
                        </option>
                      </select>
                    </div>

                    {!form.channelWhatsapp && (
                      <p className="muted" style={{ marginTop: "0.4rem" }}>
                        {t("botFeatures.leadAds.whatsapp.requiresWhatsapp")}
                      </p>
                    )}
                  </div>
                </label>
              </div>
            </fieldset>

            <button className="btn-primary" type="submit" disabled={saving}>
              {saving
                ? t("botFeatures.actions.saving")
                : t("botFeatures.actions.save")}
            </button>
          </form>
        </section>

                <section className="detail-side">
          <div className="plan-summary-header">
            <h2 style={{ margin: 0 }}>{t("botFeatures.side.title")}</h2>
            <span className={getStatusPillClass(bot.status)}>
              {t("botFeatures.side.statusLabel")} <strong>{bot.status}</strong>
            </span>
          </div>

          <p className="muted" style={{ marginTop: "0.35rem" }}>
            {t("botFeatures.side.subtitle")}
          </p>



          {!isActive && (
            <>
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={nextLoading || saving}
                style={{ marginTop: "1rem" }}
              >
                {nextLoading
                  ? t("botFeatures.actions.saving")
                  : t("botFeatures.actions.next")}
              </button>

              <p className="detail-side-note">
                {t("botFeatures.side.nextNote")}
              </p>
            </>
          )}

          {isActive && (
            <>
              <button
                className="btn-primary"
                onClick={() => navigate(`/app/bots/${bot.id}/plan`)}
                style={{ marginTop: "0.75rem" }}
              >
                {t("botFeatures.actions.viewChangePlan")}
              </button>

              <button
                className="btn-secondary"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                style={{ marginTop: "0.6rem" }}
              >
                {cancelLoading
                  ? t("botFeatures.actions.cancelling")
                  : t("botFeatures.actions.cancelSubscription")}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default BotFeaturesPage;
